import apiFetch from "@/app/auth/apiFetch";

const API_URL = import.meta.env.VITE_API_URL;

export interface PluginStatus {
  worker_id: string;
  ip: string;
  /** UA brut du navigateur. Sert a re-deriver le navigateur (cf. formatBrowser). */
  user_agent?: string | null;
  browser_name: string;
  browser_version: string;
  os_name: string;
  /** Version OS brute (plugin v1.7+, Client Hints platformVersion). null sur
   * Firefox/Safari et plugins < v1.7. Formatee a l'affichage via lib/os.ts. */
  os_version?: string | null;
  user_id?: string | null;
  user_name?: string | null;
  is_anonymous: boolean;
  plugin_version?: string | null;
  last_seen: string;
  location?: {
    country?: string;
    /** ISO 3166 alpha-2 (ip-api.com). Absent des entrees cache <= juin 2026. */
    country_code?: string;
    city?: string;
    lat?: number;
    lon?: number;
    isp?: string;
  };
  linkedin_user?: {
    firstName?: string;
    lastName?: string;
    publicIdentifier?: string;
    occupation?: string;
  } | null;
  quotas?: PluginQuotas | null;
  /**
   * Quota glissant par ABONNEMENT (dimension `rolling`) — invitation/message,
   * used/cap sur 1h et 24h. C'est l'axe RÉELLEMENT enforcé pour les envois
   * (`quota:{keycloak_sub}:{bucket}:{h|d}`), donc celui qui révèle un blocage
   * "max par heure". Resettable via resetUserQuotas(user_id, ["message"|"invitation"]).
   */
  subscription_quotas?: {
    invitation: AccountQuotaBucket;
    message: AccountQuotaBucket;
  } | null;
  /** Quota par COMPTE LinkedIn (account_rolling) — scrape/search, used/cap h+d. */
  account_quotas?: AccountQuotas | null;
  /**
   * ISO timestamp (UTC, RFC3339) du dernier POST `/api/worker/next-job` capté
   * côté back pour ce `worker_id`. `null` quand le plugin envoie son heartbeat
   * mais ne demande pas de jobs (cas : tab LinkedIn fermée, content script
   * crashé, Service Worker zombie). Distinct de `last_seen` qui ne prouve que
   * la vie du Service Worker, pas son activité réelle.
   */
  last_next_job_call?: string | null;
  /**
   * Type de job retourné au dernier next-job. `"quota_blocked"` = bucket saturé.
   * `"auth_rejected"` = Bearer refused, the poll was answered 401.
   */
  last_job_type?:
    | "campaign_action"
    | "enrichment"
    | "search"
    | "company_enrichment"
    | "school_enrichment"
    | "company_jobs"
    | "profile_rescrape"
    | "none"
    | "quota_blocked"
    | "paused"
    | "lease_blocked"
    | "auth_rejected"
    | null;
  /**
   * `false` = the last /next-job was not authenticated. Why: see
   * `next_job_auth_header` (no header at all) and `next_job_auth_rejected`.
   */
  next_job_authenticated?: boolean | null;
  /**
   * The last /next-job carried an Authorization header. Absent on traces
   * written by back pods that predate header tracing.
   */
  next_job_auth_header?: boolean | null;
  /** Why the header of the last /next-job did not authenticate. */
  next_job_auth_rejected?: "expired" | "invalid" | "other" | string | null;
  /**
   * Worker state computed by the backoffice for this snapshot — the same
   * classifier as the « Par état » chart (keys: see `STATE_META`,
   * app/plugin-status/workerState.ts). `null` = activity layer unreadable,
   * never « inactive » by default. Absent on an older backoffice.
   */
  worker_state?: string | null;
  /** Heartbeat age in seconds, server clock (never the admin browser's). */
  last_seen_age_s?: number | null;
  /** Age of the last /next-job in seconds, server clock. `null` without activity. */
  activity_age_s?: number | null;
  /**
   * Start (epoch ms, plugin clock) of the job the plugin is running, `null`
   * when none (plan 5.4). No /next-job poll happens during a job: the
   * backoffice classifies the worker `busy`, not `stalled`, while the job is
   * younger than its timeout. Absent from plugins that predate it.
   */
  job_running_since?: number | null;
  /** Age of the running job in seconds, server clock. `null` when none runs. */
  job_running_age_s?: number | null;
  /** Age of `last_worker_error` in seconds, server clock. */
  last_worker_error_age_s?: number | null;
  /**
   * Backoffice verdict of the ⚠ rule (plan 5.1): an error younger than 30 min
   * and not transient. Absent on an older backoffice.
   */
  last_worker_error_active?: boolean | null;
  /** Bucket quota saturé si `last_job_type === "quota_blocked"`. */
  blocked_bucket?: "invitation" | "message" | "scrape" | "search" | "system" | null;
  /** Tier anti-détection LinkedIn calculé côté back au dernier next-job. */
  tier?: "new" | "trusted" | null;
  /** Mode de pause admin actif au dernier next-job (`last_job_type === "paused"`). */
  paused_mode?: "scraping" | "all" | string | null;
  /**
   * Session LinkedIn déclarée par le plugin au dernier next-job (cookies
   * li_at+JSESSIONID). `false` = capability-gate : le worker polle mais ne
   * reçoit aucun job tant qu'il est déconnecté de LinkedIn. Absent sur les
   * plugins legacy (< Partie A).
   */
  has_linkedin_session?: boolean | null;
  /**
   * Dernière erreur capturée côté plugin et persistée dans
   * `chrome.storage.local.lastWorkerError`, puis remontée via heartbeat.
   * Visible sans devoir attacher la DevTools du Service Worker (qui perd
   * sa session au sommeil MV3).
   */
  last_worker_error?: {
    source: string;          // "process_next_job" | "inject_send_message" | etc.
    reason: string;          // "ping_failed" | "worker_unavailable" | "tab_load_timeout" | ...
    ts: number;              // epoch ms (Date.now() côté plugin)
    message?: string | null;
    /** error | warning | transient (plugin v1.8+ ; défaut error en amont). */
    severity?: string | null;
    /** Stack JS quand une vraie Error est attrapée (plugin v1.8+). */
    stack?: string | null;
    /** Sac de diagnostic : tabUrl, tabStatus, action, jobType, retryCount… */
    context?: Record<string, unknown> | null;
    /** Compteur d'échecs consécutifs au moment de l'erreur. */
    consecutive_failures?: number | null;
  } | null;
  /** Échecs de jobs consécutifs (compteur worker, plugin v1.4+). */
  consecutive_job_failures?: number | null;
  /**
   * Timestamps (epoch ms) du dernier tire de chaque Chrome Alarm. Permet de
   * distinguer "alarme zombie" (stale >> periodInMinutes) de "alarme tire +
   * code planté ensuite" (timestamp frais + last_worker_error présent).
   */
  last_alarm_fired_at?: {
    heartbeat?: number | null;
    job_check?: number | null;
  } | null;
  /** Période actuelle de l'alarme JOB côté plugin (debug régression backoff). */
  job_check_period_minutes?: number | null;
  /**
   * Flag de pause « Vérification LinkedIn requise » remonté par le plugin via
   * heartbeat (cadence 15-60s). Présent (`active: true`) tant que le worker est
   * en pause sur un interstitiel de sécurité LinkedIn (checkpoint / challenge /
   * authwall / login) — l'automatisation reprend une fois la vérification
   * résolue côté navigateur. `null` sinon. Distinct du kill-switch sticky
   * `/linkedin-error` (Tier 3) : ici c'est l'état transitoire « en pause ».
   */
  linkedin_checkpoint?: {
    active: boolean;
    /** CHECKPOINT | LOGIN | AUTHWALL */
    type?: string | null;
    /** epoch ms — première détection (« en pause depuis… »). */
    detected_at?: number | null;
    /** epoch ms — dernier rafraîchissement du flag côté plugin. */
    last_seen_at?: number | null;
  } | null;
  /**
   * Kill-switch scraping admin (worker_settings.scraping_disabled). `true` =
   * ce plugin ne reçoit plus de jobs de scraping (enrichissements, recherches,
   * offres) — les invitations/messages de campagne continuent.
   */
  scraping_disabled?: boolean;
  /**
   * Kill-switch scraping du COMPTE (subscriptions.scraping_disabled) — coupe
   * tous les plugins authentifiés du compte. `null` pour un plugin anonyme
   * (pas de subscription → seul le toggle par plugin s'applique).
   */
  account_scraping_disabled?: boolean | null;
}

export interface PluginQuotaCounter {
  used: number;
  limit: number;
  remaining: number;
}

export interface PluginQuotas {
  invites: PluginQuotaCounter;
  messages: PluginQuotaCounter;
  counter_started_at: string | null;
  resets_at: string;
  stale: boolean;
}

/**
 * Conso/cap d'un bucket (scrape|search) sur une fenêtre, axe quota par COMPTE
 * LinkedIn (dimension `account_rolling`). `used_*` = null si Redis indisponible.
 */
export interface AccountQuotaBucket {
  used_hourly: number | null;
  cap_hourly: number | null;
  used_daily: number | null;
  cap_daily: number | null;
}

/**
 * Quota par COMPTE LinkedIn (anti-ban scraping, y compris anonyme). Clé Redis
 * `quota:{account_key}:{bucket}:{h|d}`. `account_key` = `li:{publicId}` (compte
 * identifié, resettable) ou `wk:{worker_id}` (worker anonyme non rattaché).
 * `enforcement_enabled=false` => phase shadow : les compteurs se remplissent
 * mais aucun worker n'est bloqué (back `WORKER_ACCOUNT_QUOTA_ENABLED`).
 */
export interface AccountQuotas {
  account_key: string;
  public_identifier: string | null;
  tier: "new" | "trusted" | string;
  enforcement_enabled: boolean;
  buckets: {
    scrape: AccountQuotaBucket;
    search: AccountQuotaBucket;
  };
}

/** Une catégorie d'un chart de répartition (navigateur / OS / version). */
export interface PluginAggregateBucket {
  label: string;
  count: number;
}

/** Idem pour la localisation, avec de quoi rendre le drapeau côté front. */
export interface PluginCountryBucket extends PluginAggregateBucket {
  /** Nom anglais ip-api.com (null pour "Inconnu" / "Réseau privé"). */
  country?: string | null;
  /** ISO 3166 alpha-2 si connu (sert au drapeau). */
  country_code?: string | null;
}

/**
 * A bucket of the « Par état » chart. `key` = stable English identifier computed
 * by the backoffice (`_classify_worker_state`, which also feeds each row's
 * `worker_state`); the French label + semantic colour are resolved at display
 * time (STATE_META, app/plugin-status/workerState.ts).
 */
export interface PluginStateBucket {
  key: string;
  count: number;
}

/**
 * Répartition de TOUTE la flotte connectée, calculée côté backoffice (pas le
 * client : on ne télécharge plus la flotte entière juste pour la graphique).
 * Compte authoritatif ; le repli "top-N + Autres" est fait à l'affichage.
 */
export interface PluginAggregates {
  by_country: PluginCountryBucket[];
  by_browser: PluginAggregateBucket[];
  by_os: PluginAggregateBucket[];
  by_version: PluginAggregateBucket[];
  /**
   * Mutually exclusive fleet states (active, inactive, quota…), including the
   * workers with an activity trace but no heartbeat — absent from the table:
   * `ghost` (still polling) and `offline` (gone). `[]` when the Redis activity
   * layer is unavailable (the chart is then hidden, never « all silent » by
   * mistake). Optional: rolling backoffice deploy.
   */
  by_state?: PluginStateBucket[];
}

export interface PluginStatusResponse {
  /** Plugins de la PAGE demandée (ou toute la flotte si non paginé). */
  plugins: PluginStatus[];
  /** Nombre TOTAL de plugins connectés (toute la flotte, pas la page). */
  total: number;
  /** Page courante (1-indexée) renvoyée par le back. */
  page?: number;
  page_size?: number | null;
  /** Distributions fleet-wide pour les 4 charts. */
  aggregates?: PluginAggregates;
  /** Workers en pause « Vérification LinkedIn » — fleet-wide (bannière). */
  paused?: PluginStatus[];
}

/**
 * Récupère les plugins connectés. Pagination opt-in : passer `{page, pageSize}`
 * pour ne charger qu'une page (le tableau admin) ; sans options, renvoie toute
 * la flotte (carte dashboard + supervision workers).
 */
export async function getConnectedPlugins(
  navigate,
  opts?: { page?: number; pageSize?: number },
): Promise<PluginStatusResponse> {
  const params = new URLSearchParams();
  if (opts?.page) params.set("page", String(opts.page));
  if (opts?.pageSize) params.set("page_size", String(opts.pageSize));
  const qs = params.toString();
  const res = await apiFetch(
    `${API_URL}/plugin-status/connected${qs ? `?${qs}` : ""}`,
    { method: "GET" },
    navigate,
  );
  if (!res?.ok) throw new Error("Erreur lors du chargement des plugins connectés");
  return res.json();
}
